import React, { useEffect, useState } from 'react';
import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useQueryClient } from '@tanstack/react-query';
import { IAdminOrganization, IAdminTeam, createTeam, useAdminOrganizationsQuery } from '../../api/admin';
import { apiErrorMessage } from '../../utils/api';

interface Props {
	open: boolean;
	onClose: () => void;
	/** Pre-select this organization (e.g. when opened from an organization page). */
	defaultOrganizationId?: number;
}

export default function CreateTeamDialog({ open, onClose, defaultOrganizationId }: Props) {
	const queryClient = useQueryClient();
	const { data: organizations, isLoading: organizationsLoading } = useAdminOrganizationsQuery();

	const [name, setName] = useState('');
	const [description, setDescription] = useState('');
	const [organization, setOrganization] = useState<IAdminOrganization | null>(null);
	const [nameError, setNameError] = useState<string | null>(null);
	const [organizationError, setOrganizationError] = useState<string | null>(null);
	const [submitting, setSubmitting] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [created, setCreated] = useState<IAdminTeam | null>(null);

	// Pre-select the default organization once the list has loaded.
	useEffect(() => {
		if (open && defaultOrganizationId !== undefined && organizations && organization === null) {
			const match = organizations.find((o) => o.id === defaultOrganizationId) ?? null;
			if (match) setOrganization(match);
		}
	}, [open, defaultOrganizationId, organizations, organization]);

	function resetAndClose() {
		setName('');
		setDescription('');
		setOrganization(null);
		setNameError(null);
		setOrganizationError(null);
		setError(null);
		setCreated(null);
		onClose();
	}

	async function handleSubmit() {
		let valid = true;
		if (!name.trim()) {
			setNameError('Required');
			valid = false;
		}
		if (!organization) {
			setOrganizationError('Required');
			valid = false;
		}
		if (!valid || !organization) return;

		setSubmitting(true);
		setError(null);
		try {
			const team = await createTeam({
				name: name.trim(),
				organization_id: organization.id,
				description: description.trim() || null,
			});
			await Promise.all([
				queryClient.invalidateQueries(['admin', 'teams']),
				queryClient.invalidateQueries(['admin', 'organizations']),
			]);
			setCreated(team);
		} catch (e: any) {
			setError(apiErrorMessage(e, 'An unexpected error occurred. Please try again.'));
		} finally {
			setSubmitting(false);
		}
	}

	const noOrganizations = !organizationsLoading && (organizations ?? []).length === 0;

	return (
		<Dialog
			open={open}
			onClose={resetAndClose}
			maxWidth="sm"
			fullWidth
			PaperProps={{ sx: { backgroundImage: 'none' } }}
		>
			{created ? (
				<>
					<DialogTitle>Team Created</DialogTitle>
					<DialogContent>
						<Alert severity="success">
							<strong>{created.name}</strong> was created in <strong>{created.organization_name}</strong>.
							Open the team to add users.
						</Alert>
					</DialogContent>
					<DialogActions>
						<Button variant="contained" onClick={resetAndClose}>
							Done
						</Button>
					</DialogActions>
				</>
			) : (
				<>
					<DialogTitle>New Team</DialogTitle>
					<DialogContent sx={{ pb: 1 }}>
						{error && (
							<Alert severity="error" sx={{ mb: 2 }}>
								{error}
							</Alert>
						)}
						{noOrganizations && (
							<Alert severity="warning" sx={{ mb: 2 }}>
								No organizations exist yet. Create one from the Organizations tab first.
							</Alert>
						)}
						<Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
							Every team belongs to exactly one organization.
						</Typography>
						<Stack spacing={2}>
							<Autocomplete
								options={organizations ?? []}
								loading={organizationsLoading}
								value={organization}
								onChange={(_, value) => {
									setOrganization(value);
									setOrganizationError(null);
								}}
								getOptionLabel={(o) => o.name}
								isOptionEqualToValue={(a, b) => a.id === b.id}
								renderInput={(params) => (
									<TextField
										{...params}
										label="Organization"
										size="small"
										required
										error={!!organizationError}
										helperText={organizationError}
										InputProps={{
											...params.InputProps,
											endAdornment: (
												<>
													{organizationsLoading && <CircularProgress color="inherit" size={16} />}
													{params.InputProps.endAdornment}
												</>
											),
										}}
									/>
								)}
							/>
							<TextField
								label="Team Name"
								size="small"
								required
								value={name}
								onChange={(e) => {
									setName(e.target.value);
									setNameError(null);
								}}
								error={!!nameError}
								helperText={nameError}
								onKeyDown={(e) => {
									if (e.key === 'Enter') handleSubmit();
								}}
							/>
							<TextField
								label="Description (optional)"
								size="small"
								multiline
								minRows={2}
								value={description}
								onChange={(e) => setDescription(e.target.value)}
							/>
						</Stack>
					</DialogContent>
					<DialogActions>
						<Button onClick={resetAndClose} disabled={submitting}>
							Cancel
						</Button>
						<Button
							variant="contained"
							onClick={handleSubmit}
							disabled={submitting || !name.trim() || !organization}
							startIcon={submitting ? <CircularProgress size={16} /> : undefined}
						>
							{submitting ? 'Creating…' : 'Create Team'}
						</Button>
					</DialogActions>
				</>
			)}
		</Dialog>
	);
}
